import { withFeatureFlag } from 'utils/featureFlagHelpers';
import { isEmptyString } from 'utils/stringHelpers';
import { LeftTooltip } from 'ui/tooltip';
import { BASIC_SELECT_SIZES, BaseOption } from 'ui/selects';
import { HUMAN_READABLE_ITERABLE_EVENTS } from 'constants/eventTypes';

import { useCustomBehavioralContext, useConversionInsightsContext } from './contexts';
import { getEventInfo } from './utils';
import { InsightSelect } from './InsightSelect';

import { BehavioralEvent } from '../domain';
import { useTranslation } from '../hooks/useTranslation';
import { AUDIENCE_INSIGHTS_HIGHEST_CAMPAIGNS_FEATURE_FLAG } from '../featureFlags';

export const CONVERSION_RATE_DATA_TEST = {
  select: 'conversion-rate-select',
};

const constructConversionEventOptions = (customEvents: BehavioralEvent[]) => {
  const allConversionOption = { id: 'All conversions', label: 'All conversions', value: '' };

  const eventOptions = customEvents.map<BaseOption<string>>(({ event, dataType }) => ({
    id: `conversion-rate-${event}-${dataType}`,
    label: getEventInfo(dataType, HUMAN_READABLE_ITERABLE_EVENTS, event),
    value: event,
  }));

  const options = eventOptions.length ? [allConversionOption, ...eventOptions] : [];
  return { options, eventOptions };
};

export const ConversionRateSelect = withFeatureFlag(
  AUDIENCE_INSIGHTS_HIGHEST_CAMPAIGNS_FEATURE_FLAG,
  () => {
    const { t } = useTranslation();
    const { allCustomEvents } = useCustomBehavioralContext();
    const { updateConversion } = useConversionInsightsContext();

    const placeholder = t('insights.select.conversionRatePlaceholder');
    const searchPlaceholder = t('insights.select.filterPlaceholder');
    const tooltipContent = t('insights.select.conversionTooltip');

    const { options, eventOptions } = constructConversionEventOptions(allCustomEvents);

    return (
      <LeftTooltip isEnabled={eventOptions.length === 0} tooltipContent={tooltipContent}>
        <InsightSelect
          selectTestId={CONVERSION_RATE_DATA_TEST.select}
          selectPlaceholder={placeholder}
          isDisabled={eventOptions.length === 0}
          size={BASIC_SELECT_SIZES.SMALL}
          selectOptions={options}
          onOptionSelect={([value]) => {
            const strValue = `${value}`;
            // if all conversions is selected we want to update with null
            const event = isEmptyString(strValue) ? null : strValue;
            updateConversion(event);
          }}
          textInputPlaceholder={searchPlaceholder}
        />
      </LeftTooltip>
    );
  },
);
